"use client";

import { useEffect } from "react";
import { Star, X } from "lucide-react";
import type { StoreReview } from "@/types";

const AVATAR_COLORS = [
  "bg-green-600", "bg-violet-600", "bg-red-500", "bg-teal-600",
  "bg-blue-600", "bg-amber-500", "bg-rose-500", "bg-indigo-600",
  "bg-emerald-600", "bg-orange-500", "bg-cyan-600", "bg-pink-500",
];

function avatarColor(name: string): string {
  let hash = 0;
  for (const ch of name) hash = ch.charCodeAt(0) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function formatReviewDate(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Aujourd'hui";
  if (diffDays === 1) return "Hier";
  if (diffDays < 7) return `Il y a ${diffDays} j`;
  if (diffDays < 30) return `Il y a ${Math.floor(diffDays / 7)} sem.`;
  if (diffDays < 365) return `Il y a ${Math.floor(diffDays / 30)} mois`;
  return `Il y a ${Math.floor(diffDays / 365)} an${Math.floor(diffDays / 365) > 1 ? "s" : ""}`;
}

function StarFull({ className }: { className?: string }) {
  return <Star className={className ?? "h-4 w-4"} fill="currentColor" />;
}

export function ReviewDetailModal({ review, onClose }: { review: StoreReview; onClose: () => void }) {
  const initial = (review.customer.firstname?.[0] ?? "?").toUpperCase();
  const color = avatarColor(review.customer.firstname + review.customer.lastname);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full sm:max-w-lg bg-white dark:bg-[#2A2A2A] sm:rounded-2xl rounded-t-2xl flex flex-col max-h-[85vh] overflow-hidden">
        {/* Handle bar (mobile) */}
        <div className="flex sm:hidden justify-center pt-3 pb-1">
          <div className="h-1 w-10 rounded-full bg-gray-300 dark:bg-[#444]" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-gray-100 dark:border-[#313131]">
          <div className="flex items-center gap-3">
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${color}`}>
              {initial}
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-white">
                {review.customer.firstname} {review.customer.lastname?.slice(0, 1)}.
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500">
                {review.customer.reviews_count} Avis · {formatReviewDate(review.created_at)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 dark:border-[#313131] text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#313131] transition"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {/* Stars + score */}
          <div className="flex items-center gap-3">
            <span className="text-4xl font-extrabold text-gray-900 dark:text-white leading-none">
              {Number(review.rating).toFixed(1)}
            </span>
            <div className="flex items-center gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <StarFull key={i} className={`h-5 w-5 ${i < review.rating ? "text-yellow-400" : "text-gray-200 dark:text-gray-700"}`} />
              ))}
            </div>
          </div>

          {/* Full comment */}
          {review.comment ? (
            <p className="text-[15px] leading-relaxed text-gray-700 dark:text-gray-300">{review.comment}</p>
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500 italic">Aucun commentaire</p>
          )}

          {/* Ordered items */}
          {review.order_items.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Produits commandés</p>
              <div className="flex flex-wrap gap-2">
                {review.order_items.map((item) => (
                  <span
                    key={item.ulid}
                    className="rounded-full border border-gray-200 dark:border-[#313131] bg-gray-50 dark:bg-[#313131] px-3 py-1.5 text-[13px] font-medium text-gray-700 dark:text-gray-300"
                  >
                    {item.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
